import React, { useState, useEffect } from "react";
import "../styles/Requisitions.scss";
import { Link, useNavigate } from "react-router-dom";
import { FaList, FaUserPlus, FaTrash, FaEye } from "react-icons/fa";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  getAllRequisitions,
  getAssignedJobsForRecruiter,
  deleteRequisition,
  getUsersByRoleId,
  assignRecruiters,
  getAllRecruiters,
} from "../services/api";
import ExcelJS from "exceljs";

const ITEMS_PER_PAGE = 10;

function Requisitions() {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [recruiterModalOpen, setRecruiterModalOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedRecruiter, setSelectedRecruiter] = useState("");
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Get role_id and user data from localStorage
  const roleId = localStorage.getItem("role_id");
  const userData = localStorage.getItem("userData");
  const userId = userData ? JSON.parse(userData)?.user?.id : null;

  // For get all job requisitions data
  useEffect(() => {
    const fetchRequisitions = async () => {
      try {
        let res;

        // 📌 Recruiter role (roleId === "4")
        if (roleId === "4") {
          if (!userId) {
            console.error("User ID not found for recruiter");
            return;
          }

          // 🔄 Get jobs assigned to this recruiter
          res = await getAssignedJobsForRecruiter(userId);
        } else {
          // 🔄 For other roles, fetch all job requisitions
          res = await getAllRequisitions();
        }

        // ✅ If response is successful and contains array
        if (res?.result_code === 200 && Array.isArray(res.result_info)) {
          setRequisitions(res.result_info);
        } else {
          console.warn("No requisitions found or unexpected format");
          setRequisitions([]);
        }
      } catch (err) {
        console.error("Error fetching requisitions:", err);

        // Optional: Show SweetAlert on failure

        Swal.fire({
          icon: "error",
          title: "Failed to load requisitions",
          text: "Something went wrong. Please try again later.",
          confirmButtonColor: "#d33",
        });
      }
    };

    fetchRequisitions();
  }, [roleId, userId]);

  // Fetch recruiters for role_id 3 (Hiring Manager)
  useEffect(() => {
    if (roleId === "3") {
      const fetchRecruiters = async () => {
        try {
          const res = await getAllRecruiters();
          if (res?.status === "S" && Array.isArray(res.result_info)) {
            setRecruiterOptions(
              res.result_info.map((u) => ({
                value: u.id,
                label: u.name,
                id: u.id,
                email: u.email,
              }))
            );
          } else {
            setRecruiterOptions([]);
          }
        } catch (error) {
          console.error("Error fetching recruiters:", error);
          setRecruiterOptions([]);
        }
      };
      fetchRecruiters();
    }
  }, [roleId]);

  // Handle assign recruiter
  const handleAssignRecruiter = async () => {
    if (!selectedRecruiter) {
      Swal.fire({
        icon: "warning",
        title: "Please select a recruiter",
        text: "You must select a recruiter to assign.",
        confirmButtonColor: "#d33",
      });
      return;
    }

    try {
      const payload = {
        requisition_id: selectedRequisition.id,
        requisition_correl_id: selectedRequisition.correl_id,
        recruiter_id: selectedRecruiter,
        assigned_by: userId || 1,
      };

      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      const res = await assignRecruiters(payload);
      Swal.close();

      if (res && res.status === "S") {
        Swal.fire({
          icon: "success",
          title: "Recruiter assigned successfully",
          confirmButtonColor: "#0078b9",
          confirmButtonText: "OK",
        });
        setRecruiterModalOpen(false);
        setSelectedRecruiter("");
        setSelectedRequisition(null);

        // Refresh the requisitions list
        window.location.reload();
      } else {
        Swal.fire({
          icon: "error",
          title: res?.message || "Failed to assign recruiter",
          confirmButtonColor: "#0078b9",
        });
      }
    } catch (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Failed to assign recruiter",
        text: "Something went wrong. Please try again.",
        confirmButtonColor: "#0078b9",
      });
    }
  };

  // Open assign recruiter modal
  const openAssignRecruiterModal = (requisition) => {
    setSelectedRequisition(requisition);
    setSelectedRecruiter("");
    setRecruiterModalOpen(true);
  };

  // Helper to format date as dd-mm-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Filter requisitions based on search input
  // Search in Job ID, Job Title, Department, and Recruiter
  const filteredRequisitions = requisitions.filter((r) => {
    const searchTerm = search.toLowerCase();
    
    // Search in Job ID
    const jobIdMatch = (r.job_id || r.id || "").toString().toLowerCase().includes(searchTerm);
    
    // Search in Job Title
    const jobTitleMatch = (r.job_title || "").toLowerCase().includes(searchTerm);
    
    // Search in Department
    const departmentMatch = r.department_details && 
      r.department_details.some((dept) =>
        (dept.name || "").toLowerCase().includes(searchTerm)
      );
    
        // Search in Recruiter
    const recruiterMatch = r.recruiter_details && 
      r.recruiter_details.some((rec) =>
        (rec.name || "").toLowerCase().includes(searchTerm)
      );

    // Search in Created Date
    const dateMatch = r.created_at && 
      formatDate(r.created_at).toLowerCase().includes(searchTerm);

    if (roleId === "4") {
      // For recruiters, search in Job ID, Job Title, and Created Date
      return jobIdMatch || jobTitleMatch || dateMatch;
    } else {
      // For other roles, search in all fields: Job ID, Job Title, Department, Recruiter, and Created Date
      return jobIdMatch || jobTitleMatch || departmentMatch || recruiterMatch || dateMatch; 
    }
  });

  const pageCount = Math.ceil(filteredRequisitions.length / ITEMS_PER_PAGE);
  const paginatedRequisitions = filteredRequisitions.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // For delete Requisitions
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the requisition.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await deleteRequisition(id);
        setRequisitions((prev) => prev.filter((r) => r.id !== id));
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Requisition has been deleted.",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete requisition. Please try again.",
        });
      }
    }
  };

  

  const getNamesFromDetails = (details) => {
    return Array.isArray(details) && details.length > 0
      ? details.map((d) => d.name).join(", ")
      : "--";
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Requisitions Report");

    roleId === "4"
      ? // Add header row
        (worksheet.columns = [
          { header: "Job ID", key: "job_id" },
          { header: "Job Title", key: "job_title" },
          { header: "Created At", key: "created_at" },
        ])
      : (worksheet.columns = [
          { header: "Job ID", key: "job_id" },
          { header: "Job Title", key: "job_title" },
          { header: "Department", key: "department" },
          { header: "Recruiter", key: "recruiter" },
          { header: "Created At", key: "created_at" },
        ]);
    // Add data rows
    requisitions.forEach((item, index) => {
      worksheet.addRow({
        job_id: item.job_id || item.id,
        job_title: item.job_title || "--",
        department: getNamesFromDetails(item.department_details),
        recruiter: getNamesFromDetails(item.recruiter_details),
        created_at: item.created_at ? formatDateForCSV(item.created_at) : "--",
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Requisitions.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  // React.useEffect(() => {
  //   if(!search){
  //     // setPage(1);
  //     localStorage.removeItem("searchTerm");
  //     localStorage.removeItem("page");
  //     setSearch("");
  //     setPage(1);
  //   }
  // }, [search]);

  

  // Helper to format date for CSV export (dd/mm/yyyy)
  const formatDateForCSV = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper to get display text for arrays
  const getDisplayText = (details, fallback) => {
    if (details && Array.isArray(details) && details.length > 0) {
      return details.map((item) => item.name).join(", ");
    }
    return fallback || "-";
  };

  function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  const handleNavigate = () => {
    localStorage.setItem("searchTerm", search);
    localStorage.setItem("page", page.toString());
    // navigate("/requisitions/assignedlist");
  };

  useEffect(() => {
    const storedSearch = localStorage.getItem("searchTerm");
    const storedPage = localStorage.getItem("page");

    if (storedSearch) setSearch(storedSearch);
    if (storedPage) setPage(Number(storedPage));
  }, []);

  return (
    <div className="requisitions-page">
      <div className="requisitions-header-row">
        <div className="requisitions-title-group">
          <h1>{roleId === "4" ? "Assigned Requisitions" : "Requisitions"}</h1>
          {/* <p>
            {roleId === "4"
              ? "View and manage your assigned job requisitions"
              : "Manage and track all job requisitions"}
          </p> */}
        </div>
        <button className="export-btn" onClick={handleExport}>
          Export
        </button>
      </div>
      <div className="requisitions-table-controls">
        <input
          className="custom-placeholder"
          placeholder={
            roleId === "4"
              ? "Search by Job ID, Job Title, or Date"
              : "Search by Job ID, Job Title, Department, Recruiter, or Date"
          }
          value={search}
          style={{
            minWidth: 500,
            maxWidth: 300,
            padding: "0.4rem .5rem",
            border: "1.5px solid #d1d5db",
            borderRadius: 6,
            background: "#fff",
            fontSize: "1rem",
          }}
          
          onChange={(e) => {
            setSearch(e.target.value)
            if(!e.target.value){
              // setPage(1);
              localStorage.removeItem("searchTerm");
              localStorage.removeItem("page");
              setSearch("");
              setPage(1);
            }
          }}
        />
        {/* Hide Create Requisition button for role_id 4 */}
        {roleId === "3" && (
          <Link to="/requisitions/create" className="create-btn">
            Create Requisition
          </Link>
        )}
      </div>
      <div className="requisitions-table-wrapper">
        <table className="requisitions-table">
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Job Title</th>
              {roleId !== "4" && <th>Department</th>}
              {roleId !== "4" && <th>Recruiter</th>}
              {/* <th>Openings</th> */}
              {/* <th>Status</th> */}
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRequisitions.length === 0 ? (
              <tr>
                <td
                  colSpan={roleId === "4" ? 5 : 7}
                  style={{ textAlign: "center" }}
                >
                  {roleId === "4"
                    ? "No assigned requisitions found"
                    : "No requisitions found"}
                </td>
              </tr>
            ) : (
              paginatedRequisitions.map((requisition) => (
                <tr key={requisition.id}>
                  <td>{requisition.job_id || requisition.id}</td>
                  <td>{requisition.job_title}</td>
                  {roleId !== "4" && (
                    <td>
                      {getDisplayText(
                        requisition.department_details,
                        requisition.departments
                      )}
                    </td>
                  )}
                  {roleId !== "4" && (
                    <td>
                      {getDisplayText(
                        requisition.recruiter_details,
                        requisition.recruiters
                      )}
                    </td>
                  )}
                  {/* <td>{requisition.openings}</td> */}
                  {/* <td>
                    <span
                      className={`status-badge ${requisition.status
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {requisition.status}
                    </span>
                  </td> */}
                  <td>
                    {requisition.created_at
                      ? formatDate(requisition.created_at)
                      : "-"}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {/* Role-based action buttons */}
                      {roleId === "1" && (
                        <>
                          {/* View button for role_id 1 */}
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                              {
                                handleNavigate();
                                navigate(`/requisitions/edit/${requisition.id}`)
                              }
                            }
                            title="View"
                          >
                            <FaEye />
                          </button>
                          {/* Delete button for role_id 1 */}
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(requisition.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              handleNavigate();
                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_id: requisition.id,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: requisition.id,
                                    jobTitle: requisition.job_title,
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              });
                            }}

                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}

                      {roleId === "4" && (
                        <>
                          {/* View button for role_id 4 */}
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                            {
                              handleNavigate();
                              navigate(`/requisitions/edit/${requisition.id}`)
                            }
                            }
                            title="View Requisition"
                          >
                            <FaEye />
                          </button>
                          {/* Assign Candidate button for role_id 4 */}
                          <button
                            className="assign-btn"
                            style={{
                              background: "transparent",
                              color: "#28a745",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                            {
                              handleNavigate();
                              navigate("/requisitions/assign", {
                                state: {
                                  jobId: requisition.id,

                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: requisition.id,
                                    jobTitle: requisition.job_title,

                                    location: requisition.locations || "-",
                                    // departments: getDisplayText(
                                    //   requisition.department_details,
                                    //   requisition.departments
                                    // ),
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              })
                            }}
                            title="Assign Candidates"
                          >
                            <FaUserPlus />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                            {handleNavigate()
                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_id: requisition.id,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: requisition.id,
                                    jobTitle: requisition.job_title,
                                    // location: getDisplayText(
                                    //   requisition.location_details,
                                    //   requisition.location
                                    // ),
                                    // department: getDisplayText(
                                    //   requisition.department_details,
                                    //   requisition.departments
                                    // ),
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              })
                            }}
                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}

                      {roleId === "3" && (
                        <>
                          {/* Edit button for role_id 3 */}
                          <button
                            className="edit-btn"
                            onClick={() =>
                            {
                              handleNavigate();
                              navigate(`/requisitions/edit/${requisition.id}`)
                            }
                              
                            }
                            title="View"
                          >
                            <FaEye />
                          </button>
                          {/* Delete button for role_id 3 */}
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(requisition.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                          {/* Assign Recruiters button for role_id 3 */}
                          <button
                            className="assign-btn"
                            style={{
                              background: "transparent",
                              color: "#28a745",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                              openAssignRecruiterModal(requisition)
                            }
                            title="Assign Recruiter"
                          >
                            <FaUserPlus />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                            { handleNavigate();
                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_id: requisition.id,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: requisition.id,
                                    jobTitle: requisition.job_title,
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),

                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              })
                            }
                            }
                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
       {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
      {recruiterModalOpen && roleId === "3" && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 32,
              minWidth: 500,
              width: "400px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
            }}
          >
            <h5 style={{ marginBottom: 18, fontWeight: 600 }}>
              Assign Recruiter
            </h5>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ fontWeight: 500, marginBottom: 6, display: "block" }}
              >
                Select Recruiter *
              </label>
              <Select
                options={recruiterOptions}
                value={
                  selectedRecruiter
                    ? recruiterOptions.find(
                        (option) => option.value === selectedRecruiter
                      )
                    : null
                }
                onChange={(selected) =>
                  setSelectedRecruiter(selected ? selected.value : "")
                }
                isSearchable={true}
                placeholder="Select a recruiter..."
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 6,
                    border: "1.5px solid #d1d5db",
                    minHeight: "42px",
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#6b7280",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isDisabled
                      ? "#f3f4f6"
                      : state.isFocused
                      ? "#e5e7eb"
                      : "white",
                    color: state.isDisabled ? "#9ca3af" : "#374151",
                    cursor: state.isDisabled ? "not-allowed" : "default",
                  }),
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 20,
                gap: 12,
              }}
            >
              <button
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                onClick={() => {
                  setRecruiterModalOpen(false);
                  setSelectedRecruiter("");
                  setSelectedRequisition(null);
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#0078b9",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                onClick={handleAssignRecruiter}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Requisitions;
